/**
 * Operation Run Policy (TV1-1828, T21)
 *
 * Drives the side effects of the operations review surface for a single
 * `OperationRun` (identified by `runId`):
 *
 * 1. **Query-first initial load.** On init, immediately fetches the run
 *    lifecycle row via `backend.queries.OperationsRunsById( runId )` and then
 *    the change set via `backend.queries.OperationsChangeSetsById( runId )`.
 *    This is the reliable path — the pv3 signal-mode subscription delivers
 *    `{ type: 'signal' }` for subsequent changes only (no data payload, no
 *    guaranteed snapshot delivery). The query fires once when the policy mounts
 *    (i.e. after `navigate( operationReviewPath( runId ))` resolves), at which
 *    point the `OperationProposed` event is guaranteed to be projected (the
 *    in-process E2ELocalOutbox + MemoryDb projector are synchronous with the
 *    command round-trip).
 * 2. **Signal subscription for live updates.** Subscribes to
 *    `OperationRunsReadmodel` with `_changes: 'signal'` so the review surface
 *    reacts to status changes as apply progresses. On each signal the policy
 *    debounces a refetch of the run lifecycle row AND the change set (per-row
 *    statuses advance as items are applied). The snapshot delivered on
 *    subscription open is ignored — the explicit query-first load (1) covers it.
 * 3. **Approval submission.** Reacts to `loading.submitting` flipping true while
 *    the run is still `ready` and dispatches the approve endpoint carrying the
 *    approved `targetId`s. The endpoint (T13, `POST /operations/:runId/approve`)
 *    computes `approvedHash` SERVER-SIDE from `approvedItemIds`, so the front-end
 *    sends ONLY `approvedItemIds` — see the header note below.
 * 4. **Rejection submission.** Reacts to `loading.submitting` flipping true once
 *    the operator has chosen to reject (no approved rows) and dispatches the
 *    reject endpoint.
 * 5. **Apply progress.** Surfaced by reusing the run lifecycle counts
 *    (`appliedCount` / `failedCount` / `totalItems`) which the signal-triggered
 *    run-by-id refetch keeps fresh. `selectors.applyProgressPercentage` derives
 *    the bar from those counts.
 *
 * ── Approve-path decision (CRITICAL) ──────────────────────────────────────────
 * The generated `backend.post.approve_operation(( runId ), { approvedItemIds })`
 * endpoint accepts ONLY `approvedItemIds` and returns
 * `{ runId, approvedHash, approvedCount }` — i.e. T13 mints `approvedHash`
 * server-side. We therefore send only `approvedItemIds`. (The lower-level
 * aggregate command `backend.commands.ApproveOperation` requires `approvedHash`
 * in its body, but the front-end does not use it — the dedicated egress-gated
 * HTTP endpoint is the supported caller and owns the hashing.)
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import {
  ClientLibraryType,
  ReadmodelChange
} from '@bett3r-dev/teselly-client-library';
import {
  OperationRunsReadmodelType,
  OperationChangeSetReadmodelType
} from '@bett3r-dev/teselly-domain';
import { OperationsState, selectors } from '../state';
import { OperationsCommand } from '../commands';

const SIGNAL_DEBOUNCE_MS = 250;

type DispatchFn = ( cmd: OperationsCommand, meta: { source: string }) => void;

export const createOperationRunPolicy = (
  backend: ClientLibraryType,
  runId: string
): Policy<OperationsState, OperationsCommand> => {
  let closeSubscription: { close: () => void } | null = null;
  let signalTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  // Latest lifecycle row for this run. Seeded by the initial query-first fetch
  // and refreshed by the run-by-id query on each signal. Supplies the
  // change-set's run-level metadata (the change-set query returns only `{ items }`).
  let latestRun: OperationRunsReadmodelType | null = null;

  /** Refetch the change set and dispatch it into the review surface. */
  const fetchChangeSet = async ( dispatch: DispatchFn ): Promise<void> => {
    if ( closed ) return;
    dispatch(
      { type: 'operations/loading/setChangeSet', payload: { isLoading: true }},
      { source: 'api' }
    );
    try {
      // Distinct change-set query → `GET /operations/change-sets/:id` → `{ items }`.
      const result = await backend.queries.OperationsChangeSetsById( runId );
      if ( closed ) return;
      // The change-set query carries only the items; the run-level metadata
      // comes from the lifecycle row (query-first fetch, or the run-by-id refetch
      // on signal).
      const changeSet: OperationChangeSetReadmodelType = {
        id: runId,
        accountId: latestRun?.accountId ?? '',
        ...( latestRun?.ownerId ? { ownerId: latestRun.ownerId } : {}),
        operationType: latestRun?.operationType ?? 'reprice',
        riskClass: latestRun?.riskClass ?? 'read',
        // The client library types `cells` map values as
        // `{ old: Record<string,never>; new: Record<string,never> } | undefined`
        // — the openapi generator's representation of the `BaseSchema<any>` cell
        // fields plus an optional index-signature value. The runtime shape is
        // identical to the domain `ChangeCell` (`{ old?: unknown; new?: unknown }`),
        // so we cast at this client-lib↔domain boundary (same pattern the other FE
        // policies use when assigning query results to readmodel types).
        items: result.items as OperationChangeSetReadmodelType['items']
      };
      dispatch(
        { type: 'operations/review/setChangeSet', payload: { changeSet }},
        { source: 'api' }
      );
    } catch ( error ) {
      if ( closed ) return;
      console.error( 'Error fetching operation change set:', error );
      dispatch(
        { type: 'operations/loading/setChangeSet', payload: { isLoading: false }},
        { source: 'api' }
      );
    }
  };

  /**
   * Refetch the run lifecycle row by id and dispatch it. Signal-mode carries no
   * data, so the run-by-id query (`GET /operations/runs/:id`, returns the full
   * `OperationRunsReadmodelType`) is the only fresh source of status + counts.
   */
  const fetchRun = async ( dispatch: DispatchFn ): Promise<void> => {
    if ( closed ) return;
    try {
      const run = await backend.queries.OperationsRunsById( runId );
      if ( closed || !run ) return;
      latestRun = run;
      dispatch(
        { type: 'operations/review/loadRun', payload: { run }},
        { source: 'api' }
      );
    } catch ( error ) {
      if ( closed ) return;
      console.error( 'Error fetching operation run:', error );
    }
  };

  /** Refetch both the run lifecycle row and the change set after a signal. */
  const refetchOnSignal = ( dispatch: DispatchFn ): void => {
    if ( signalTimer ) clearTimeout( signalTimer );
    signalTimer = setTimeout( async () => {
      signalTimer = null;
      if ( closed ) return;
      // pv3 signal-mode delivers no data, so this policy owns the refetch.
      // Refresh the run lifecycle first so the change-set query reads fresh
      // run-level metadata, then refetch the change set (per-row statuses +
      // counts advance as apply progresses).
      await fetchRun( dispatch );
      if ( closed ) return;
      await fetchChangeSet( dispatch );
    }, SIGNAL_DEBOUNCE_MS );
  };

  return {
    name: 'operations-operation-run',

    init: ({ dispatch }) => {
      dispatch(
        { type: 'operations/loading/setRun', payload: { isLoading: true }},
        { source: 'api' }
      );

      // Query-first: immediately fetch the run lifecycle row and the change set
      // via explicit one-shot queries. This is the reliable initial load path —
      // the pv3 signal-mode subscription may suppress the snapshot delivery, and
      // the run is guaranteed to be projected by the time the HTTP response from
      // `propose_operation` returns (E2ELocalOutbox + MemoryDb are synchronous).
      // We chain fetchChangeSet after fetchRun so `latestRun` is populated before
      // the change-set query needs it for run-level metadata.
      ( async () => {
        await fetchRun( dispatch );
        if ( closed ) return;
        await fetchChangeSet( dispatch );
      })();

      // Subscribe to the run lifecycle for SUBSEQUENT changes only (apply progress,
      // status transitions). Signal-mode delivers `{ type: 'signal' }` with no
      // data; the snapshot that arrives on subscription open is ignored here
      // because the query-first load above already covers initial state.
      closeSubscription = backend.subscriptions.OperationsRuns(
        { id: runId, _changes: 'signal' },
        ( change: ReadmodelChange<OperationRunsReadmodelType> ) => {
          if ( !change || closed ) return;
          // Ignore the snapshot — query-first load handles initial state.
          if ( change.type === 'signal' ) {
            refetchOnSignal( dispatch );
          }
        }
      );

      return () => {
        closed = true;
        if ( signalTimer ) {
          clearTimeout( signalTimer );
          signalTimer = null;
        }
        if ( closeSubscription ) {
          closeSubscription.close();
          closeSubscription = null;
        }
      };
    },

    // React when a submission begins (submitting flips false → true). The same
    // flag is set by both `submitApproval` and `reject`; the approved-rows count
    // disambiguates which backend call to make.
    shouldReact: ( prev, next ) =>
      !prev.loading.submitting && next.loading.submitting,

    onStateChange: async ({ next, ctx }) => {
      const approvedItemIds = selectors.approvedItemIds( next );
      const isApproval = approvedItemIds.length > 0;

      try {
        if ( isApproval ) {
          // Approve: T13 endpoint computes `approvedHash` server-side from
          // `approvedItemIds`; the front-end sends only the approved subset.
          await backend.post.approve_operation(
            { runId },
            { approvedItemIds }
          );
        } else {
          // Reject: nothing applied; terminal.
          await backend.post.reject_operation({ runId }, {});
        }

        if ( closed ) return;
        ctx.dispatch(
          { type: 'operations/loading/setSubmitting', payload: { isLoading: false }},
          { source: 'api' }
        );
        // The lifecycle subscription will deliver a signal when the status
        // changes (approved / applying / applied / rejected) and the
        // debounced refetch will refresh both the run row and change set.
      } catch ( error ) {
        if ( !closed ) {
          ctx.dispatch(
            { type: 'operations/loading/setSubmitting', payload: { isLoading: false }},
            { source: 'api' }
          );
        }
        // Re-throw for the global error handler to surface the notification.
        throw error;
      }
    }
  };
};
